import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingRow } from "../components/EmptyState";

interface EmailTemplate {
  id: string;
  business_id: string;
  key: string;
  language: string;
  label: string;
  subject: string;
  body_text: string;
  body_html: string | null;
  is_active: number;
  version: number;
  ab_variant: string;
  created_at: string;
  updated_at: string;
}

interface TemplateStats {
  template_key: string;
  ab_variant: string | null;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
}

interface TemplateVersion {
  id: string;
  template_id: string;
  version: number;
  label: string;
  subject: string;
  body_text: string;
  body_html: string | null;
  is_active: number;
  created_at: string;
}

interface EditorState {
  id: string | null; // null = creating a new template
  key: string;
  language: string;
  ab_variant: string;
  label: string;
  subject: string;
  body_text: string;
  body_html: string;
  is_active: boolean;
}

function emptyEditor(): EditorState {
  return {
    id: null,
    key: "",
    language: "en",
    ab_variant: "A",
    label: "",
    subject: "",
    body_text: "",
    body_html: "",
    is_active: true,
  };
}

function editorFromTemplate(t: EmailTemplate): EditorState {
  return {
    id: t.id,
    key: t.key,
    language: t.language,
    ab_variant: t.ab_variant,
    label: t.label,
    subject: t.subject,
    body_text: t.body_text,
    body_html: t.body_html ?? "",
    is_active: t.is_active === 1,
  };
}

export default function Templates() {
  const qc = useQueryClient();
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [testEmail, setTestEmail] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  const templates = useQuery({
    queryKey: ["tenant", "templates"],
    queryFn: () =>
      apiFetch<{ templates: EmailTemplate[] }>("/api/tenant/templates"),
  });

  const stats = useQuery({
    queryKey: ["tenant", "templates", "stats"],
    queryFn: () =>
      apiFetch<{ stats: TemplateStats[] }>("/api/tenant/templates/stats"),
  });

  const statsFor = (t: EmailTemplate): TemplateStats | undefined =>
    stats.data?.stats.find(
      (s) => s.template_key === t.key && s.ab_variant === t.ab_variant,
    );

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["tenant", "templates"] });

  const versions = useQuery({
    queryKey: ["tenant", "templates", editor?.id, "versions"],
    enabled: !!editor?.id,
    queryFn: () =>
      apiFetch<{ versions: TemplateVersion[] }>(
        `/api/tenant/templates/${editor!.id}/versions`,
      ),
  });

  const restore = useMutation({
    mutationFn: (args: { id: string; version: number }) =>
      apiFetch<{ ok: true; template: EmailTemplate }>(
        `/api/tenant/templates/${args.id}/restore`,
        {
          method: "POST",
          body: JSON.stringify({ version: args.version }),
        },
      ),
    onSuccess: (data) => {
      setEditor(editorFromTemplate(data.template));
      setNotice(`Restored — now v${data.template.version}.`);
      invalidate();
      qc.invalidateQueries({
        queryKey: ["tenant", "templates", data.template.id, "versions"],
      });
    },
  });

  const save = useMutation({
    mutationFn: (e: EditorState) => {
      const payload = {
        key: e.key.trim(),
        language: e.language.trim(),
        ab_variant: e.ab_variant.trim().toUpperCase() || "A",
        label: e.label.trim(),
        subject: e.subject,
        body_text: e.body_text,
        body_html: e.body_html.trim() || null,
        is_active: e.is_active ? 1 : 0,
      };
      return e.id
        ? apiFetch(`/api/tenant/templates/${e.id}`, {
            method: "PATCH",
            body: JSON.stringify(payload),
          })
        : apiFetch("/api/tenant/templates", {
            method: "POST",
            body: JSON.stringify(payload),
          });
    },
    onSuccess: () => {
      setEditor(null);
      setNotice("Template saved.");
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/tenant/templates/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setEditor(null);
      setNotice("Template deleted.");
      invalidate();
    },
  });

  const testSend = useMutation({
    mutationFn: (args: { id: string; to: string }) =>
      apiFetch(`/api/tenant/templates/${args.id}/test-send`, {
        method: "POST",
        body: JSON.stringify({ to: args.to }),
      }),
    onSuccess: () => setNotice(`Test email sent to ${testEmail}.`),
  });

  if (templates.isLoading) return <LoadingRow />;
  if (templates.error)
    return <ErrorBanner message={(templates.error as Error).message} />;

  const rows = templates.data?.templates ?? [];
  const activeError =
    (save.error as Error | null) ??
    (remove.error as Error | null) ??
    (testSend.error as Error | null) ??
    (restore.error as Error | null);

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Email templates</h1>
          <p className="mt-1 text-sm text-slate-500">
            The emails your receptionist can send. Use{" "}
            <code className="rounded bg-slate-100 px-1">{"{{caller_name}}"}</code>,{" "}
            <code className="rounded bg-slate-100 px-1">{"{{business.calendly_url}}"}</code>{" "}
            and friends — they're filled in per conversation. Keys starting with{" "}
            <code className="rounded bg-slate-100 px-1">_</code> are system
            templates (booking confirmations etc.) and can't be deleted.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setNotice(null);
            setEditor(emptyEditor());
          }}
          className="shrink-0 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          New template
        </button>
      </div>

      {notice && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">
          {notice}
        </div>
      )}
      {activeError && <ErrorBanner message={activeError.message} />}

      {rows.length === 0 && !editor && (
        <EmptyState
          title="No templates yet"
          hint="Create the emails the receptionist can offer to send callers."
        />
      )}

      {rows.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Key</th>
                <th className="px-4 py-2">Lang</th>
                <th className="px-4 py-2">Var</th>
                <th className="px-4 py-2">Label</th>
                <th className="px-4 py-2">Subject</th>
                <th className="px-4 py-2">v</th>
                <th className="px-4 py-2">Performance</th>
                <th className="px-4 py-2">Active</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {rows.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono text-xs">{t.key}</td>
                  <td className="px-4 py-2">{t.language}</td>
                  <td className="px-4 py-2">{t.ab_variant}</td>
                  <td className="px-4 py-2">{t.label}</td>
                  <td className="max-w-xs truncate px-4 py-2 text-slate-500">
                    {t.subject}
                  </td>
                  <td className="px-4 py-2 text-slate-400">v{t.version}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    <PerformanceCell stats={statsFor(t)} />
                  </td>
                  <td className="px-4 py-2">{t.is_active ? "✓" : "—"}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      className="text-brand-600 hover:underline"
                      onClick={() => {
                        setNotice(null);
                        setEditor(editorFromTemplate(t));
                      }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editor && (
        <form
          className="space-y-4 rounded-xl border border-slate-200 bg-white p-6"
          onSubmit={(e) => {
            e.preventDefault();
            setNotice(null);
            save.mutate(editor);
          }}
        >
          <h2 className="text-lg font-semibold text-slate-900">
            {editor.id ? `Edit "${editor.key}" (${editor.language})` : "New template"}
          </h2>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <Field label="Key *" hint="Identifier the AI picks, e.g. 'pricing'.">
              <Input
                value={editor.key}
                onChange={(v) => setEditor({ ...editor, key: v })}
                required
              />
            </Field>
            <Field label="Language *" hint="e.g. 'en' or 'es'.">
              <Input
                value={editor.language}
                onChange={(v) => setEditor({ ...editor, language: v })}
                required
              />
            </Field>
            <Field
              label="Variant"
              hint="A/B letter. Active variants of one key are sent at random."
            >
              <Input
                value={editor.ab_variant}
                onChange={(v) => setEditor({ ...editor, ab_variant: v })}
              />
            </Field>
            <Field label="Label *" hint="Shown to the AI when choosing.">
              <Input
                value={editor.label}
                onChange={(v) => setEditor({ ...editor, label: v })}
                required
              />
            </Field>
          </div>

          <Field label="Subject *">
            <Input
              value={editor.subject}
              onChange={(v) => setEditor({ ...editor, subject: v })}
              required
            />
          </Field>
          <Field label="Body (plain text) *">
            <Textarea
              value={editor.body_text}
              onChange={(v) => setEditor({ ...editor, body_text: v })}
              rows={8}
              required
            />
          </Field>
          <Field label="Body (HTML)" hint="Optional branded version. Leave empty to send plain text only.">
            <Textarea
              value={editor.body_html}
              onChange={(v) => setEditor({ ...editor, body_html: v })}
              rows={6}
            />
          </Field>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={editor.is_active}
              onChange={(e) =>
                setEditor({ ...editor, is_active: e.target.checked })
              }
            />
            Active (the AI can only send active templates)
          </label>

          <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-4">
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {save.isPending ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => setEditor(null)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>

            {editor.id && (
              <>
                <span className="mx-2 h-5 w-px bg-slate-200" />
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  disabled={!testEmail || testSend.isPending}
                  onClick={() => {
                    setNotice(null);
                    testSend.mutate({ id: editor.id!, to: testEmail });
                  }}
                  className="rounded-lg border border-brand-600 px-4 py-2 text-sm font-medium text-brand-600 hover:bg-brand-50 disabled:opacity-50"
                >
                  {testSend.isPending ? "Sending…" : "Send test"}
                </button>

                {!editor.key.startsWith("_") && (
                  <button
                    type="button"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Delete the "${editor.key}" (${editor.language}) template? The AI will no longer be able to send it.`,
                        )
                      ) {
                        setNotice(null);
                        remove.mutate(editor.id!);
                      }
                    }}
                    className="ml-auto rounded-lg border border-red-300 px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    Delete
                  </button>
                )}
              </>
            )}
          </div>

          {editor.id && (versions.data?.versions.length ?? 0) > 1 && (
            <div className="border-t border-slate-100 pt-4">
              <h3 className="text-sm font-semibold text-slate-700">
                Version history
              </h3>
              <ul className="mt-2 divide-y divide-slate-100 text-sm">
                {versions.data!.versions.map((v) => (
                  <li
                    key={v.id}
                    className="flex items-center justify-between gap-4 py-2"
                  >
                    <div className="min-w-0">
                      <span className="font-mono text-xs text-slate-500">
                        v{v.version}
                      </span>{" "}
                      <span className="truncate text-slate-700">
                        {v.subject}
                      </span>
                      <span className="ml-2 text-xs text-slate-400">
                        {v.created_at}
                      </span>
                    </div>
                    {v.version !==
                      versions.data!.versions[0]?.version && (
                      <button
                        type="button"
                        disabled={restore.isPending}
                        onClick={() => {
                          setNotice(null);
                          restore.mutate({ id: editor.id!, version: v.version });
                        }}
                        className="shrink-0 text-brand-600 hover:underline disabled:opacity-50"
                      >
                        Restore
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </form>
      )}
    </div>
  );
}

/** "12 sent · 67% open · 25% click", with bounce/complaint flags when
 *  present. Rates are against delivered (or sent if delivery events are
 *  not yet flowing). */
function PerformanceCell({ stats }: { stats?: TemplateStats }) {
  if (!stats || stats.sent === 0) return <span>—</span>;
  const base = stats.delivered > 0 ? stats.delivered : stats.sent;
  const pct = (n: number) => `${Math.round((n / base) * 100)}%`;
  return (
    <span>
      {stats.sent} sent · {pct(stats.opened)} open · {pct(stats.clicked)} click
      {stats.bounced > 0 && (
        <span className="ml-1 text-red-600">· {stats.bounced} bounced</span>
      )}
      {stats.complained > 0 && (
        <span className="ml-1 text-red-600">· {stats.complained} spam</span>
      )}
    </span>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Input({
  value,
  onChange,
  required,
}: {
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
    />
  );
}

function Textarea({
  value,
  onChange,
  rows = 3,
  required,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  required?: boolean;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      required={required}
      className="block w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
    />
  );
}
